import { useTranslate } from 'react-polyglot'
import { PRODUCT_TYPES, SHOPIFY_CUSTOM_ATTRIBUTES } from '~/lib/constants'

import { fromProductCardPropsToProductTrackingData } from '~/hooks/tracking/useTrackProductCardsList'
import { getUserLabels } from '~/hooks/useGetAddToCartProductProps'
import useLocale from '~/hooks/useLocale'

import { getIdFromGid } from '~/utils/shopify-ids'

import {
  CUSTOMIZER,
  CUSTOM_ATTRIBUTES,
  PRODUCTS_TYPES,
} from '~/data/dictionary'
import serializePrice from '~/data/serialize-price'

export function useGetBundleCartProps({
  isMultiBundle,
  multiSku,
  multiId,
  data,
  userSelection,
  userLabels,
  customizationImageState,
}) {
  const t = useTranslate()
  const locale = useLocale()

  const bundlesProps = userSelection.map((tab, index) => {
    // our bundle tab for multiId products
    const activeTab = userSelection[index]

    // serialized main product data
    const productData =
      data?.[index]?.find(({ shopifyId }) => shopifyId === tab.productId) ??
      null

    // current main product variant
    const variantId = tab.variantId
    // serialized main product variant data
    const currentVariant = productData?.variants?.find(
      ({ id }) => id === variantId,
    )

    const isCustomizable = !!productData?.customization

    // serialized customization products data
    const customizerProducts = productData?.customizationProducts ?? []

    // serialized customization active variants data
    const customizerVariants = customizerProducts
      .map((product) => ({
        type: product.productType,
        variant: product.variants.find(
          (variant) => variant.id === activeTab[product.productType],
        ),
      }))
      .filter(({ variant }) => Boolean(variant))

    /* Creating a string that is the sku of the current variant and the sku of the main customization
      product. */
    const rootSku = `${currentVariant?.sku}${
      customizerVariants?.[0]?.variant?.sku
        ? `#${customizerVariants?.[0]?.variant?.sku}`
        : ''
    }`

    const getTrackingData = (product, customizedLabel) => {
      return fromProductCardPropsToProductTrackingData(
        {
          name: product?.productName ?? product?.title ?? null,
          categories: product?.categories ?? null,
          meanings: product?.meanings ?? null,
          shopifyId: product?.shopifyId ?? null,
          defaultVariant: product?.defaultVariant ?? null,
          price: product?.defaultVariant?.price,
          personalizedLabel: customizedLabel ? true : false,
          compareAtPrice: product?.defaultVariant?.compareAtPrice ?? null,
          media: product?.media ?? [],
          link: product?.link ?? null,
        },
        {},
      )
    }

    const labelCustomizationProduct =
      productData?.labelCustomizationProducts?.find(
        (product) => product.shopifyId === tab.labelCustomization,
      )
    const isBundle = Boolean(
      productData?.labelCustomizationProducts?.length > 0 ||
        productData?.customizationProducts?.length > 0,
    )

    const labelCustomizationVariant = labelCustomizationProduct?.variants?.find(
      (variant) => variant.id === tab?.labelCustomizationVariant,
    )

    const getBundlePrice = () => {
      let totalAmount = isMultiBundle ? 0 : +currentVariant?.price?.amount

      if (labelCustomizationVariant)
        totalAmount += +labelCustomizationVariant?.price?.amount
      customizerVariants?.forEach((variant) => {
        const customizerVariantsPrice = +(variant?.variant?.price?.amount ?? 0)
        totalAmount += customizerVariantsPrice
      })

      return {
        price:
          serializePrice(
            locale,
            currentVariant?.price?.currencyCode,
            totalAmount,
          ) ?? null,
        amount: totalAmount,
      }
    }
    const bundlePrice = getBundlePrice()
    const customizerMainVariantName =
      customizerVariants?.[0]?.variant?.selectedOptions?.[0]?.value

    /* Creating a new product name based on the product name, the main customization product, and the
    label customization. */
    const newProductName = `${productData?.productName}${
      customizerMainVariantName ? `/${customizerMainVariantName}` : ''
    }${
      labelCustomizationVariant?.selectedOptions?.[0]?.value
        ? `/${labelCustomizationVariant?.selectedOptions?.[0]?.value}`
        : '/Non personalisé'
    }`

    /* This one is the legacy id used in bycharlot checkout */
    const bundleId = `${getIdFromGid(tab.variantId)}:${Date.now()}:${
      Math.floor(Math.random() * (95000 - 10000)) + 10000
    }`

    /* Checking if there is a customizer product or a label customization. */
    const hasOption = Boolean(
      !!customizerProducts?.find((product) => product?.activeVariant) ||
        tab?.labelCustomizationVariant,
    )

    const customizedLabel = userLabels[index]

    const activeTabLabelImage = customizationImageState[index]
    const labelRawDataAttributes = userLabels[index]
      ? getUserLabels(customizedLabel, activeTabLabelImage)
      : []

    // This identifier allows to correctly bind our bundles in the cart
    const bundleIdentifier = Buffer.from(
      `${variantId}${customizerProducts
        ?.map((product) => product?.activeVariant?.id)
        ?.join('')}${customizedLabel?.formattedSubtitle ?? ''}${
        customizedLabel?.formattedTitle ?? ''
      }
        `,

      // ${/*postPreviewData?.activeVariant?.id ?? ''*/}
    ).toString('base64')

    // const customizationsAttributes = customizerVariants?.map(
    //   ({ variant, type }) => ({
    //     key: t(PRODUCTS_TYPES.TYPE(type)),
    //     value: variant?.title,
    //   }),
    // )

    const trackingData = getTrackingData(
      productData,
      labelCustomizationVariant?.selectedOptions?.[0]?.value,
    )

    const addtoCartCustomizationProps = customizerVariants
      ?.map(({ variant, type }, i) => {
        return {
          id: variant?.id ?? null,
          attributes: [
            { key: '_sku', value: variant?.sku },
            ...(isMultiBundle ? [{ key: '_multiId', value: multiId }] : []),
            {
              key: '_bundleId',
              value: bundleId,
            },
            {
              key: '_bundleIdentifier',
              value: bundleIdentifier,
            },
            ...(variant?.isBundleProductPreOrder
              ? [
                  {
                    key: '_isBundleProductPreOrder',
                    value: `${variant?.isBundleProductPreOrder}`,
                  },
                ]
              : []),
            // {
            //   key: t(PRODUCTS_TYPES.TYPE(type)),
            //   value: variant?.title,
            // },
            {
              key: SHOPIFY_CUSTOM_ATTRIBUTES.trackingData,
              value: JSON.stringify(
                getTrackingData(customizerProducts[i], false),
              ),
            },
            ...(type?.toLowerCase() !== PRODUCT_TYPES.POT.toLowerCase() &&
            type?.toLowerCase() !== PRODUCT_TYPES.PUCK &&
            type?.toLowerCase() !== PRODUCT_TYPES.COVER.toLowerCase()
              ? [
                  {
                    key: '_upsellType',
                    value: t(PRODUCTS_TYPES.TYPE(type)).toLocaleLowerCase(),
                  },
                  {
                    key: '_isUpsell',
                    value: 'true',
                  },
                ]
              : []),
            ...(type === PRODUCT_TYPES.POT || type === PRODUCT_TYPES.COVER
              ? [
                  {
                    key: '_hasOption',
                    value: String(hasOption),
                  },
                ]
              : []),
          ],
        }
      })
      .filter(Boolean)

    const addToCartLabelCustomization = {
      id: tab.labelCustomizationVariant,
      attributes: [
        ...labelRawDataAttributes,
        {
          key: '_bundleId',
          value: bundleId,
        },
        {
          key: '_bundleIdentifier',
          value: bundleIdentifier,
        },
        {
          key: SHOPIFY_CUSTOM_ATTRIBUTES.trackingData,
          value: JSON.stringify(
            getTrackingData(labelCustomizationProduct, false),
          ),
        },
        {
          key: '_rootSKU',
          value: rootSku,
        },
      ],
    }

    /* Checking if the bundle product is available for sale and if the customizer products are available for
    sale. */
    const bundleAvailableForSale =
      (currentVariant?.availableForSale &&
        customizerVariants
          ?.filter((p) => p?.variant)
          ?.every((variant) => variant?.variant?.availableForSale)) ??
      false

    /* Checking if the product is a bundle or not. If it is a bundle, it will check if the bundle is
    available for sale. If it is not a bundle, it will check if the current variant is available for
    sale. */
    const isAvailableForSale = isBundle
      ? bundleAvailableForSale
      : currentVariant?.availableForSale

    return {
      disabled: !isAvailableForSale,
      availableForSale: isAvailableForSale,
      price: bundlePrice,
      merchandiseId: variantId,
      customizations: customizerProducts ? addtoCartCustomizationProps : null,
      ...(tab.labelCustomization
        ? { labelCustomization: addToCartLabelCustomization }
        : {}),
      attributes: [
        { key: '_sku', value: currentVariant?.sku },
        ...(isMultiBundle
          ? [
              { key: '_multiId', value: multiId },
              { key: '_multiSku', value: multiSku },
            ]
          : []),
        {
          key: SHOPIFY_CUSTOM_ATTRIBUTES.trackingData,
          value: JSON.stringify(trackingData),
        },
        ...(productData?.isSimpleProductPreOrder
          ? [
              {
                key: '_isSimpleProductPreOrder',
                value: `${productData?.isSimpleProductPreOrder}`,
              },
            ]
          : []),
        ...(isCustomizable
          ? [
              {
                key: '_is_customizable',
                value: `${isCustomizable}`,
              },
            ]
          : []),
        {
          key: '_subtitle',
          value: productData?.smallDescription ?? '',
        },
        {
          key: '_storyblok-slug',
          value: productData?.slug,
        },
        ...(isBundle || (!isBundle && isMultiBundle)
          ? [
              {
                key: '_rootBundleId',
                value: bundleId,
              },
              {
                key: '_rootBundleIdentifier',
                value: bundleIdentifier,
              },
              {
                key: '_engraving',
                value: String(
                  labelCustomizationProduct?.productType ===
                    PRODUCT_TYPES.ENGRAVING,
                ),
              },
              {
                key: '_noCustom',
                value: String(
                  labelCustomizationProduct?.productType !==
                    PRODUCT_TYPES.ENGRAVING &&
                    labelCustomizationProduct?.productType !==
                      PRODUCT_TYPES.LABEL &&
                    !productData?.hasDefaultLabelCustomization,
                ),
              },
              {
                key: t(CUSTOMIZER.PERSONALIZATION),
                value: labelCustomizationVariant?.selectedOptions?.[0].value
                  ? labelCustomizationVariant?.selectedOptions?.[0].value
                  : productData?.hasDefaultLabelCustomization
                  ? 'Standard'
                  : '',
              },
              {
                key: '_isCustomized',
                value: labelCustomizationVariant?.selectedOptions?.[0].value
                  ? 'true'
                  : 'false',
              },
              ...(customizedLabel
                ? [
                    {
                      key: t(CUSTOM_ATTRIBUTES.LABEL_TITLE),
                      value: customizedLabel?.rawTitle,
                    },
                    {
                      key: t(CUSTOM_ATTRIBUTES.LABEL_TEXT),
                      value: customizedLabel?.rawSubTitle,
                    },
                  ]
                : []),
              ...(customizedLabel ? labelRawDataAttributes : []),
              // ...customizationsAttributes,
              ...(rootSku ? [{ key: '_rootSKU', value: rootSku }] : []),
              ...(hasOption
                ? [{ key: '_hasOption', value: String(hasOption) }]
                : []),

              {
                key: SHOPIFY_CUSTOM_ATTRIBUTES.tags,
                value: productData?.tags
                  ? JSON.stringify(productData.tags)
                  : '',
              },

              ...(newProductName && isBundle
                ? [{ key: '_newProductName', value: newProductName }]
                : []),

              {
                key: '_currentImgVisual',
                value: isBundle
                  ? customizerVariants?.[0]?.variant?.variantImage?.src?.split(
                      '?',
                    )[0] ??
                    currentVariant?.image?.src?.split('?')[0] ??
                    ''
                  : currentVariant?.image?.src?.split('?')[0] ?? '',
              },
            ]
          : []),
      ],
    }
  })

  return bundlesProps
}
